// src/context/AuthContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { signOut } from 'aws-amplify/auth';
import config from '../../src/config';
import { useToast } from '../../src/hooks/use-toast';




const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const { toast } = useToast();
 
  const parseJwt = useCallback((token) => {
    try {
      // Decode the payload part of the JWT
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      console.error("Failed to parse JWT:", e);
      return null; // Return null on failure
    }
  }, []);

  const isTokenValid = useCallback((decodedToken) => {
    // Check if the token has an expiration claim
    if (!decodedToken?.exp) return false;
    // Get the current time in seconds
    const nowInSeconds = Date.now() / 1000;
    // Check if the token's expiration time is in the future
    return decodedToken.exp > nowInSeconds;
  }, []);

  // loginFromToken remains the same
  const loginFromToken = useCallback(
    async (idToken, access = null, refresh = null, userInfoFromLogin) => {

      if (typeof idToken !== 'string' || !idToken) {
        console.error("loginFromToken was called with an invalid or missing ID token.");
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Could not establish a session due to a missing token."
        });
        return; // Stop execution immediately
      }
      if (!userInfoFromLogin) {
        console.error("loginFromToken called without user info. Aborting.");
        setLoading(false);
        return;
      }
      const decodedIdToken = parseJwt(idToken);
      const userGroups = decodedIdToken['cognito:groups'] || [];
      const resolvedUserInfo = { ...userInfoFromLogin, groups: userGroups };
      setUser(resolvedUserInfo);
      setToken(idToken);
      setAccessToken(access);
      setRefreshToken(refresh);

      localStorage.setItem('selyst_token', idToken);
      localStorage.setItem('selyst_user', JSON.stringify(resolvedUserInfo));
      if (access) localStorage.setItem('selyst_access_token', access);
      if (refresh) localStorage.setItem('selyst_refresh_token', refresh);
    },
    [parseJwt]
  );
  
  // --- THIS IS THE LOGOUT FUNCTION ---
  const logout = useCallback(async (onLogoutComplete) => {
    try {
      // Attempt to sign out from the Amplify library
      await signOut({ global: true });
      // If successful, call the completion callback (which shows the success toast)
      if (onLogoutComplete) {
        onLogoutComplete();
      }
    } catch (error) {
      // If Amplify's signOut fails, show an error toast to the user
      console.error("Error during sign out:", error);
      toast({
        variant: "destructive",
        title: "Sign Out Failed",
        description: error.message || "Could not clear the session. Please try clearing site data.",
      });
    } finally {
      // ALWAYS clear the application state and localStorage, even if signOut failed
      setUser(null);
      setToken(null);
      setAccessToken(null);
      setRefreshToken(null);

      localStorage.removeItem('selyst_token');
      localStorage.removeItem('selyst_user');
      localStorage.removeItem('selyst_access_token');
      localStorage.removeItem('selyst_refresh_token');
    }
  }, [toast]); // Dependency on toast ensures it's available

  const refreshSession = useCallback(async () => {
    console.log("Attempting to refresh session...");
    console.log("refreshToken:", refreshToken);
    console.log("localStorage.getItem('selyst_refresh_token'):", localStorage.getItem('selyst_refresh_token'));

    const storedRefreshToken = refreshToken || localStorage.getItem('selyst_refresh_token');

    if (!storedRefreshToken) {
      console.log("No refresh token available. Cannot refresh session.");
      await logout();
      return false;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/auth/admin/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Response from refresh:", data);
        const {  id_token: idToken, access_token: accessToken, refreshToken: newRefreshToken } = data;
        // The user info is already in state, so we pass the existing user object
        // to loginFromToken to preserve it.
        await loginFromToken(idToken, accessToken, newRefreshToken, user);
        console.log("✅ Session successfully refreshed.");
        return true;
      } else {
        console.warn("⚠️ Token refresh failed. Logging out.");
        await logout();
        return false;
      }
    } catch (error) {
      console.error("❌ Network error during token refresh. Logging out.", error);
      await logout();
      return false;
    }
  }, [refreshToken, user, loginFromToken, logout]);
  // --- END: NEW TOKEN REFRESH FUNCTION ---
  
  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const storedToken = localStorage.getItem('selyst_token');
      const storedUserString = localStorage.getItem('selyst_user');

      if (storedToken && storedUserString) {
        const decodedToken = parseJwt(storedToken);

        if (isTokenValid(decodedToken)) {
          const userToSet = JSON.parse(storedUserString);
          if (!userToSet.groups) {
             userToSet.groups = decodedToken['cognito:groups'] || [];
          }
          setUser(userToSet);
          setToken(storedToken);
          setAccessToken(localStorage.getItem('selyst_access_token'));
          setRefreshToken(localStorage.getItem('selyst_refresh_token'));
        } else {
          // --- MODIFIED: Attempt to refresh the session instead of logging out ---
          console.warn("AuthContext: Stored token has expired. Attempting refresh...");
          await refreshSession();
        }
      }
    } catch (err) {
      console.warn("⚠️ AuthContext: Could not restore session from storage.", err);
      await logout();
    } finally {
      setLoading(false);
    }
  }, [parseJwt, isTokenValid, logout, refreshSession]);

  // --- START: NEW EFFECT FOR PERIODIC REFRESH ---
  useEffect(() => {
    // Set up an interval to refresh the token periodically, e.g., every 25 minutes.
    // Cognito access tokens typically expire after 60 minutes.
    console.log("Refresh useeffect");
    const interval = setInterval(() => {
      console.log("attempting to fetch fresh tokens");
      // Only attempt to refresh if there is a user session.
      if (user && refreshToken) {
        console.log("Performing scheduled token refresh...",refreshToken);
        refreshSession();
      }
    }, 12 * 60 * 60 * 1000); // 60 minutes

    // Clear the interval when the component unmounts.
    return () => clearInterval(interval);
  }, [user, refreshToken, refreshSession]);
  // --- END: NEW EFFECT FOR PERIODIC REFRESH ---

  useEffect(() => {
    loadSession();
  }, []);

  const contextValue = useMemo(() => ({
      user,
      token,
      accessToken,
      refreshToken,
      loading,
      loginFromToken,
      logout,
      refreshSession, // Expose the refresh function if needed elsewhere
    }),
    [user, token, accessToken, refreshToken, loading, loginFromToken, logout, refreshSession]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

// NOTE: The implementations for parseJwt, isTokenValid, and logout should be copied 
// from the previous complete code block if you are building this from scratch.